"use client";

import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { ICONOIR_ICON_NAMES, type IconoirIconName } from "../lib/iconoirIcons";
import { IconPicker } from "./IconPicker";
import { IconSuggestions } from "./IconSuggestions";

/** What an `AIIconPicker` asks its `onSuggest` for. */
export interface IconSuggestionRequest {
  subject: string;
  context: string | null;
  candidates: string[];
}

interface AIIconPickerProps {
  label?: string;
  /** Currently selected icon name, or null for "no icon". */
  value: string | null;
  onChange: (value: IconoirIconName | null) => void;
  /**
   * What the icon is for, usually the item's title. Suggestions appear once
   * it's non-empty and has stopped changing for a moment.
   */
  subject: string;
  /** Anything else that helps pick an icon, such as a description or URL. */
  context?: string | null;
  /**
   * Fetches suggested icon names. Supplied by the caller so this package stays
   * free of API-client wiring; micro apps pass `suggestIconNames` from
   * `@vellum/micro-app-auth`.
   */
  onSuggest: (request: IconSuggestionRequest) => Promise<string[]>;
  /** Icon names to offer; defaults to the curated list. */
  icons?: readonly IconoirIconName[];
  errortext?: string;
}

// Long enough that a pause mid-word doesn't fire a request, short enough that
// suggestions are there by the time the user reaches the icon field.
const SETTLE_MS = 800;

/**
 * The icon field with AI suggestions: a row of suggested icons for the
 * item's subject above the full `IconPicker`. Suggestions are a shortcut, so
 * the row stays hidden until there's a subject and silently disappears if the
 * request fails.
 */
export function AIIconPicker({
  label,
  value,
  onChange,
  subject,
  context = null,
  onSuggest,
  icons = ICONOIR_ICON_NAMES,
  errortext,
}: AIIconPickerProps) {
  const trimmedSubject = subject.trim();
  const trimmedContext = context?.trim() || null;
  // One key per settled subject and context: it is both the fetch trigger
  // and the suggestion cache key.
  const input = useDebouncedValue(
    trimmedSubject ? `${trimmedSubject}|${trimmedContext ?? ""}` : "",
    SETTLE_MS,
  );

  return (
    <div className="flex flex-col gap-4">
      <IconSuggestions
        input={input}
        value={value}
        onChange={onChange}
        onSuggest={() =>
          onSuggest({
            subject: trimmedSubject,
            context: trimmedContext,
            candidates: [...icons],
          })
        }
      />
      <IconPicker
        label={label}
        value={value}
        onChange={onChange}
        icons={icons}
        errortext={errortext}
      />
    </div>
  );
}
